/**
 * Ankara İlçe Boundary'leri
 * 
 * Bu dosyaya bölge koordinatlarını ekleyebilirsiniz.
 * Format: Her bölge için polygon koordinatları [[lng, lat], [lng, lat], ...]
 * 
 * Koordinatları şu kaynaklardan alabilirsiniz:
 * - OpenStreetMap Overpass API
 * - Nominatim API
 * - GeoJSON dosyaları
 * - Harita uygulamalarından manuel olarak
 */

export const REGION_BOUNDARIES = {
  'Çankaya': [
    // Örnek koordinatlar - gerçek boundary'ler eklenecek
    // Format: [[longitude, latitude], [longitude, latitude], ...]
    // Polygon kapalı olmalı (son nokta = ilk nokta)
    [32.8, 39.9], 
    [32.9, 39.9], 
    [32.9, 40.0], 
    [32.8, 40.0], 
    [32.8, 39.9] // Kapalı polygon için son nokta = ilk nokta
  ],
  
  'Etimesgut': [
    [32.6, 39.9], 
    [32.7, 39.9], 
    [32.7, 40.0], 
    [32.6, 40.0], 
    [32.6, 39.9]
  ],
  
  'Keçiören': [
    [32.7, 40.0], 
    [32.8, 40.0], 
    [32.8, 40.1], 
    [32.7, 40.1], 
    [32.7, 40.0]
  ],
  
  'Mamak': [
    [32.8, 39.8], 
    [32.9, 39.8], 
    [32.9, 39.9], 
    [32.8, 39.9], 
    [32.8, 39.8]
  ],
  
  'Sincan': [
    [32.5, 39.9], 
    [32.6, 39.9], 
    [32.6, 40.0], 
    [32.5, 40.0], 
    [32.5, 39.9]
  ],
  
  'Yenimahalle': [
    [32.7, 39.9], 
    [32.8, 39.9], 
    [32.8, 40.0], 
    [32.7, 40.0], 
    [32.7, 39.9]
  ],
  
  'Akyurt': [],
  'Beypazarı': [],
  'Elmadağ': [],
  'Gölbaşı': [],
  'Haymana': [],
  'Kazan': [],
  'Nallıhan': [],
  'Polatlı': [],
  'Pursaklar': []
};

/**
 * Overpass API'den bölge boundary'lerini çek
 * 
 * Kullanım örneği:
 * const boundaries = await fetchRegionBoundariesFromOSM('Çankaya', 'Ankara');
 */
export const fetchRegionBoundariesFromOSM = async (districtName, cityName = 'Ankara') => {
  try {
    const overpassUrl = 'https://overpass-api.de/api/interpreter';
    
    // Overpass QL sorgusu
    const query = `
      [out:json][timeout:25];
      (
        relation["name"="${districtName}"]["admin_level"="7"]["place"="district"]["addr:city"="${cityName}"];
        relation["name"="${districtName}"]["admin_level"="7"]["addr:city"="${cityName}"];
        relation["name"="${districtName}"]["boundary"="administrative"]["admin_level"="7"];
      );
      out geom;
    `;
    
    const response = await fetch(overpassUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.elements || data.elements.length === 0) {
      console.warn(`⚠️ ${districtName} için boundary bulunamadı`);
      return null;
    }
    
    // İlk relation'ı al
    const relation = data.elements[0];
    
    if (!relation.members || relation.members.length === 0) {
      console.warn(`⚠️ ${districtName} için member bulunamadı`);
      return null;
    }
    
    // Outer way'i bul ve koordinatları çıkar
    const outerWay = relation.members.find(m => m.role === 'outer' && m.type === 'way');
    
    if (!outerWay || !outerWay.geometry) {
      console.warn(`⚠️ ${districtName} için geometry bulunamadı`);
      return null;
    }
    
    // Koordinatları [[lng, lat], ...] formatına çevir
    const coordinates = outerWay.geometry.map(point => [point.lon, point.lat]);
    
    // Polygon'ı kapat (son nokta = ilk nokta)
    if (coordinates.length > 0) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coordinates.push([first[0], first[1]]);
      }
    }
    
    console.log(`✅ ${districtName} boundary çekildi: ${coordinates.length} nokta`);
    return coordinates;
    
  } catch (error) {
    console.error(`❌ ${districtName} boundary çekme hatası:`, error);
    return null;
  }
};

/**
 * Nominatim API'den bölge boundary'lerini çek
 * 
 * Kullanım örneği:
 * const boundaries = await fetchRegionBoundariesFromNominatim('Çankaya', 'Ankara');
 */
export const fetchRegionBoundariesFromNominatim = async (districtName, cityName = 'Ankara') => {
  try {
    const nominatimUrl = 'https://nominatim.openstreetmap.org/search';
    const params = new URLSearchParams({
      q: `${districtName}, ${cityName}, Turkey`,
      format: 'geojson',
      polygon_geojson: '1',
      limit: '1'
    });
    
    const response = await fetch(`${nominatimUrl}?${params}`, {
      headers: {
        'User-Agent': 'Pronuve Park Management System'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.features || data.features.length === 0) {
      console.warn(`⚠️ ${districtName} için boundary bulunamadı`);
      return null;
    }
    
    const feature = data.features[0];
    
    if (!feature.geometry || feature.geometry.type !== 'Polygon') {
      console.warn(`⚠️ ${districtName} için polygon geometry bulunamadı`);
      return null;
    }
    
    // GeoJSON Polygon formatından koordinatları çıkar
    // GeoJSON: [[[lng, lat], [lng, lat], ...]] (outer ring)
    const coordinates = feature.geometry.coordinates[0].map(coord => [coord[0], coord[1]]);
    
    console.log(`✅ ${districtName} boundary çekildi (Nominatim): ${coordinates.length} nokta`);
    return coordinates;
    
  } catch (error) {
    console.error(`❌ ${districtName} boundary çekme hatası (Nominatim):`, error);
    return null;
  }
};

/**
 * Point-in-polygon algoritması (Ray casting)
 */
export const pointInPolygon = (point, polygon) => {
  if (!polygon || polygon.length < 3) return false;
  
  const [x, y] = point;
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    
    const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
};

/**
 * WKT POINT formatından koordinat çıkar (POINT(lng lat))
 */
export const parseWKT = (wkt) => {
  if (!wkt || typeof wkt !== 'string') return null;
  
  const match = wkt.match(/POINT\(([^)]+)\)/i);
  if (!match) return null;
  
  const coords = match[1].trim().split(/\s+/);
  if (coords.length < 2) return null;
  
  const lng = parseFloat(coords[0]);
  const lat = parseFloat(coords[1]);
  
  if (isNaN(lng) || isNaN(lat)) return null;
  
  return [lng, lat]; // [lng, lat]
};

